// Приведение адреса объявления к виду, по которому можно перейти.
//
// Живой случай: Суточно в `og:url` отдаёт `sutochno.ru:80/front/searchapp/detail/2234344` -
// без схемы и с портом по умолчанию. Парсер клал это значение в объект как есть, панель
// подставляла его в href, и браузер разбирал `sutochno.ru:` как имя протокола. Ссылка
// выглядела почти правильной и не открывалась.
//
// Правим в трёх местах сразу (парсер, чтение источников, отображение), потому что
// испорченные значения уже лежат в БД: чинить только источник поздно, чинить только UI -
// значит оставить мусор в данных, по которому потом пойдёт переразбор.

/**
 * `raw` → абсолютный http(s)-URL или `null`, если это вообще не адрес.
 *
 * Чинит два реальных изъяна разметки площадок: отсутствие схемы и порт по умолчанию
 * (`:80` для http, `:443` для https). Всё остальное - путь, параметры, якорь - сохраняется:
 * у части площадок в параметрах лежит идентификатор варианта размещения.
 */
export function toAbsoluteUrl(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (!value) return null;

  // Схема есть - только чистим порт по умолчанию.
  const parsed = tryParse(value);
  if (parsed) return stripDefaultPort(parsed);

  // Схемы нет. `//host/path` - протокол-относительный адрес, остальное пробуем как https.
  // Пути без хоста (`/detail/1`) абсолютным адресом не станут - для них честнее null.
  if (value.startsWith('/')) {
    const asProtocolRelative = value.startsWith('//') ? tryParse(`https:${value}`) : null;
    return asProtocolRelative ? stripDefaultPort(asProtocolRelative) : null;
  }
  const guessed = tryParse(`https://${value}`);
  if (!guessed || !guessed.hostname.includes('.')) return null;
  // Порт в адресе без схемы смысла не несёт: `:80` там остался от http-origin, а схему мы
  // всё равно выбираем сами. Не срезав его здесь, мы бы отдали `https://host:80/...` -
  // формально валидный адрес, который на деле никуда не ведёт.
  if (guessed.port === '80' || guessed.port === '443') guessed.port = '';
  return stripDefaultPort(guessed);
}

function tryParse(value: string): URL | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * `http://host:80/x` → `http://host/x`. Порт по умолчанию в адресе не несёт смысла, но
 * ломает сравнение ссылок: `host:80` и `host` - разные строки.
 */
function stripDefaultPort(url: URL): string {
  if ((url.protocol === 'http:' && url.port === '80') || (url.protocol === 'https:' && url.port === '443')) {
    url.port = '';
  }
  return url.toString();
}

/** Как показать адрес человеку: без схемы и без хвостового слэша. */
export function displayUrl(raw: string, max = 64): string {
  const abs = toAbsoluteUrl(raw) ?? raw;
  const bare = abs.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  if (bare.length <= max) return bare;
  const head = Math.ceil((max - 1) / 2);
  return `${bare.slice(0, head)}…${bare.slice(-(max - 1 - head))}`;
}
